import {
  Alert,
  Button,
  Col,
  Collapse,
  Descriptions,
  Form,
  Input,
  Space,
  Tag,
  Typography,
} from "antd";
import { useEffect, useState } from "react";
import { Department, MAX_PAGE_SIZE } from "../repository/data.default";
import {
  addCompanyDepartment,
  deleteCompanyDepartment,
  getDepartment,
  getUserLoginHistory,
  updateCompanyDepartment,
} from "../repository/data.repository";
import View from "../controls/View";
import Visual from "../controls/Visual";
import DynamicForm from "../controls/DynamicForm";
import useEntityEditor from "../hooks/useEntityEditor";
import { FieldConfig, renderFields } from "../controls/DescriptionsFields";
import { QueryOperation } from "../code/search-query/search-query";
import dayjs from "dayjs";
import { DepartmentForm } from "./DepartmentView.default";
import InModal from "../controls/InModal";
import { modalTitle } from "../controls/InModal.default";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";

export interface DepartmentViewProps {
  readonly uid?: string | null;
  readonly companyUid: string;
  readonly open?: boolean;
  readonly mode: "add" | "update" | "delete" | "view";
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
}

function DepartmentView(props: DepartmentViewProps) {
  const [form] = Form.useForm();
  const values = Form.useWatch<DepartmentForm>([], form);
  const [valid, setValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [history, setHistory] = useState<{
    [key: string]: number;
  }>({});
  const [item, setItem] = useState<Department | null>(null);
  const permissions = useSelector(authorizeSelectors.permissions);

  // Inline editor for the department profile (reuses the same hook + generic
  // CompanyDepartments PATCH as the modal edit). save() sends only dirty fields + uid/rowVersion.
  const editor = useEntityEditor({
    item: item as Record<string, any> | null,
    updateFn: (input) => updateCompanyDepartment(input as any),
    onSuccess: () => loadData(),
  });

  // "Department" section — Name is editable inline; Company / Tenant are read-only.
  const departmentFields: FieldConfig[] = [
    {
      key: "name",
      label: "Name",
      editable: true,
      input: <Input size="small" />,
    },
    { key: "companyName", label: "Company" },
    { key: "tenantName", label: "Tenant" },
  ];

  const auditFields: FieldConfig[] = [
    {
      key: "createdOn",
      label: "Created On",
      format: (value) =>
        value ? dayjs(value).format("DD/MM/YYYY HH:mm") : undefined,
    },
    {
      key: "updatedOn",
      label: "Updated On",
      format: (value) =>
        value ? dayjs(value).format("DD/MM/YYYY HH:mm") : undefined,
    },
  ];

  // Lifecycle

  useEffect(() => {
    if (props.open) {
      loadData();
    } else {
      reset();
    }
  }, [props.open, props.uid]);

  useEffect(() => {
    form
      .validateFields({
        validateOnly: true,
      })
      .then((value) => {
        setValid(!!Object.values(value).length);
      })
      .catch(() => {
        setValid(false);
      });
  }, [form, values, props.open]);

  // Events

  function onSubmit(): void {
    const { name } = values;
    setLoading(true);
    setError(false);
    if (props.mode === "add") {
      addCompanyDepartment({
        name,
        companyUid: props.companyUid!,
      })
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    } else if (props.mode === "delete") {
      deleteCompanyDepartment(item?.uid!)
        .then(() => {
          setLoading(false);
          props.onFinish?.();
        })
        .catch(() => {
          setLoading(false);
          setError(true);
        });
    }
  }

  function onCancel(): void {
    props.onClose?.();
  }

  // Methods

  function loadData(): void {
    if (props?.uid) {
      setLoading(true);
      setError(false);
      Promise.all([
        getDepartment(props?.uid!),
        getUserLoginHistory({
          searchQuery: {
            conditions: [
              {
                field: "departmentUid",
                operation: QueryOperation.Equal,
                values: [props?.uid],
              },
              {
                field: "timestamp",
                operation: QueryOperation.Between,
                values: [
                  dayjs().subtract(90, "days").format("YYYY-MM-DDTHH:mm:ssZ"),
                  dayjs().format("YYYY-MM-DDTHH:mm:ssZ"),
                ],
              },
            ],
          },
          pageNumber: 0,
          pageSize: MAX_PAGE_SIZE,
        }),
      ])
        .then(([item, history]) => {
          setLoading(false);
          setItem(item);
          if (props.mode == "view") {
            const date = history.data.reduce((acc, item) => {
              const date = dayjs(item.timestamp).format("YYYY-MM-DD");
              acc[date] = (acc[date] ?? 0) + 1;

              return acc;
            }, {} as any);
            setHistory(date);
          } else {
            form.setFieldsValue({
              name: item.name,
              company: {
                display: item.companyName,
                value: item.companyUid,
              },
            });
          }
        })
        .catch(() => {
          setError(true);
          setLoading(false);
        });
    } else {
      reset();
    }
  }

  function reset(): void {
    setValid(false);
    setError(false);
    setLoading(false);
    if (props.open) {
      form.resetFields();
    }
  }

  function createMode(): string {
    switch (props.mode) {
      case "add":
        return "Add ";
      case "update":
        return "Save ";
      case "delete":
        return "Delete ";
    }

    return "";
  }

  return (
    <>
      <View
        open={["view"].includes(props.mode) && props.open}
        loading={loading}
        title={"Department"}
        actions={
          permissions.can("CompanyDepartments", "edit") &&
          (editor.editing ? (
            <Space>
              {editor.saveError && <Tag color="red">Save failed</Tag>}
              <Button onClick={editor.cancelEditing} disabled={editor.saving}>
                Cancel
              </Button>
              <Button
                type="primary"
                loading={editor.saving}
                onClick={editor.save}
              >
                Save
              </Button>
            </Space>
          ) : (
            <Button type="primary" onClick={() => editor.startEditing()}>
              Edit
            </Button>
          ))
        }
        onBack={onCancel}
      >
        <Form form={editor.form} component={false}>
          <div className="flex flex-col gap-4 p-4">
            {/* Header: department name + company */}
            <div className="flex flex-col">
              <Typography.Title level={4} className="!mb-0">
                {item?.name}
              </Typography.Title>
              <Typography.Text type="secondary">
                {item?.companyName}
              </Typography.Text>
            </div>

            {/* Department + Audit sections */}
            <div className="grid grid-cols-2 gap-4">
              <Descriptions
                title="Department"
                bordered
                size="small"
                column={1}
                items={renderFields(
                  departmentFields,
                  item as Record<string, any> | null,
                  editor.editing,
                )}
              />

              <Descriptions
                title="Audit"
                bordered
                size="small"
                column={1}
                items={renderFields(
                  auditFields,
                  item as Record<string, any> | null,
                  editor.editing,
                )}
              />
            </div>

            {/* Login activity in an accordion at the bottom */}
            <Collapse
              accordion
              destroyInactivePanel
              items={[
                {
                  key: "logins",
                  label: "Login Activity (last 90 days)",
                  children: (
                    <Visual
                      data={history}
                      width="auto"
                      render={(chart, data) => {
                        chart
                          .data(
                            Object.entries(data).map(([days, logins]) => ({
                              logins,
                              days,
                            })),
                          )
                          .encode("x", "days")
                          .encode("y", "logins")
                          .scale("x", {
                            range: [0, 1],
                            min: 0,
                            max: 100,
                          })
                          .scale("y", {
                            domainMin: 0,
                            min: 0,
                            nice: true,
                          })
                          .axis("y", {
                            title: "Logins",
                          })
                          .axis("x", {
                            title: "Days",
                          })
                          .line()
                          .label({
                            text: "logins",
                            style: {
                              dx: -4,
                              dy: -10,
                            },
                          });
                        chart.point().style("fill", "white").tooltip(false);
                      }}
                    />
                  ),
                },
              ]}
            />
          </div>
        </Form>
      </View>
      <InModal
        open={["add", "delete"].includes(props.mode) && props.open}
        title={modalTitle(props.mode, "Department", undefined)}
        onCancel={onCancel}
        size={"small"}
        actions={
          <Space className="sticky bottom-0 flex flex-row items-center justify-end">
            <Button disabled={loading} onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type={"primary"}
              danger={props.mode === "delete"}
              loading={loading}
              disabled={!valid && props.mode !== "delete"}
              onClick={onSubmit}
            >
              {createMode()}
            </Button>
          </Space>
        }
      >
        <Col className="flex flex-col">
          {props.mode != "delete" && (
            <DynamicForm
              className="w-auto"
              form={form}
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "textbox",
                        name: "name",
                        label: "Name",
                        rules: [
                          {
                            required: true,
                            message: "Name is required",
                          },
                        ],
                      },
                    ],
                  },
                ],
              }}
            />
          )}
          {props.mode == "delete" && (
            <Typography.Paragraph>
              <Typography.Text>
                Continue deleting the{" "}
                <Typography.Text className="font-bold">
                  {item?.name}
                </Typography.Text>{" "}
                department?
              </Typography.Text>
            </Typography.Paragraph>
          )}
          {error && (
            <Alert
              className="mb-2"
              message="Something went wrong, try again"
              type="error"
            />
          )}
        </Col>
      </InModal>
    </>
  );
}

export default DepartmentView;
